import React, { useEffect, useRef, useState } from 'react';
import { api, StickyNote, VaultNode } from './api';
import { Icon } from './icons';

/** Tasks & Notes — one personal surface.
 *  Tasks: accepted Helm action items + manual to-dos (vault nodes, kind 'task').
 *  Notes: the private sticky-notes board. PRIVACY: both are the caller's own —
 *  every read/write is scoped to the authenticated user. */

const PALETTE: Record<string, { bg: string; edge: string }> = {
  yellow: { bg: '#fef0c1', edge: '#f4d980' },
  pink:   { bg: '#fdd7e8', edge: '#f2a9c9' },
  blue:   { bg: '#cfe4fd', edge: '#96c2f3' },
  green:  { bg: '#c9f2d6', edge: '#8fd9a8' },
  purple: { bg: '#e2dbfb', edge: '#b7a7f0' },
};
const COLORS = ['yellow', 'pink', 'blue', 'green', 'purple'];
const INK = '#20242e';       // sticky notes are paper — always dark ink, both themes
const INK_SOFT = '#5c6172';

function TasksSection({ workspaceId, onOpenChannel }: {
  workspaceId: string | null;
  onOpenChannel?: (channelId: string) => void;
}) {
  const [tasks, setTasks] = useState<VaultNode[] | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!workspaceId) { setTasks([]); return; }
    api.myTasks(workspaceId, showDone).then((r) => { if (alive) setTasks(r); }).catch(() => { if (alive) setTasks([]); });
    return () => { alive = false; };
  }, [workspaceId, showDone]);

  const add = async () => {
    const t = draft.trim();
    if (!t || !workspaceId || busy) return;
    setBusy(true);
    try {
      const n = await api.createTask(workspaceId, t);
      setTasks((c) => [n, ...(c || [])]);
      setDraft('');
    } catch { /* keep draft so nothing is lost */ }
    finally { setBusy(false); }
  };

  const toggle = async (n: VaultNode) => {
    // optimistic flip; done tasks stay visible until the filter re-runs
    setTasks((c) => (c || []).map((x) => (x.id === n.id
      ? { ...x, completed_at: x.completed_at ? null : new Date().toISOString() } : x)));
    try { await api.toggleTask(n.id); } catch {
      setTasks((c) => (c || []).map((x) => (x.id === n.id ? n : x)));
    }
  };

  const remove = async (n: VaultNode) => {
    const prev = tasks;
    setTasks((c) => (c || []).filter((x) => x.id !== n.id));
    try { await api.deleteTask(n.id); } catch { setTasks(prev); }
  };

  const open = (tasks || []).filter((t) => !t.completed_at);
  const done = (tasks || []).filter((t) => !!t.completed_at);

  return (
    <section className="fx-tasks">
      <div className="fx-tasks-bar">
        <input className="fx-text-input fx-tasks-add" placeholder="Add a task…" value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" className="fx-tasks-showdone" onClick={() => setShowDone(!showDone)}>
          {showDone ? 'Hide done' : 'Show done'}
        </button>
      </div>
      {tasks === null ? (
        <div className="fx-tasks-empty">Loading…</div>
      ) : open.length === 0 && (!showDone || done.length === 0) ? (
        <div className="fx-tasks-empty">
          No open tasks. Accept a Helm action item or add one above.
        </div>
      ) : (
        <ul className="fx-tasks-list">
          {[...open, ...(showDone ? done : [])].map((t) => (
            <li key={t.id} className={`fx-task${t.completed_at ? ' is-done' : ''}`}>
              <button type="button" className="fx-task-check" aria-label={t.completed_at ? 'Reopen task' : 'Complete task'}
                onClick={() => toggle(t)}>
                {t.completed_at ? '✓' : ''}
              </button>
              <span className="fx-task-title">{t.title}</span>
              {t.summary && <span className="fx-task-due">{t.summary}</span>}
              {t.tags?.includes('helm') && <span className="fx-task-src" title="Created from a Helm suggestion">✳</span>}
              {t.source_channel_id && onOpenChannel && (
                <button type="button" className="fx-task-jump" title="Open the conversation"
                  aria-label="Open the conversation" onClick={() => onOpenChannel(t.source_channel_id!)}>
                  <Icon name="hash" size={13} />
                </button>
              )}
              <button type="button" className="fx-task-del" title="Delete task" aria-label="Delete task"
                onClick={() => remove(t)}>
                <Icon name="trash" size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function StickyNotesView({ meName, workspaceId, onOpenChannel }: {
  meName?: string;
  workspaceId?: string | null;
  onOpenChannel?: (channelId: string) => void;
}) {
  const [notes, setNotes] = useState<StickyNote[] | null>(null);
  const [busy, setBusy] = useState(false);
  const timers = useRef<Record<string, number>>({});

  useEffect(() => {
    let alive = true;
    api.listStickyNotes().then((r) => { if (alive) setNotes(r); }).catch(() => { if (alive) setNotes([]); });
    return () => {
      alive = false;
      Object.values(timers.current).forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const add = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const n = await api.createStickyNote({ body: '', color: COLORS[(notes?.length || 0) % COLORS.length] });
      setNotes((c) => [n, ...(c || [])]);
      // focus the new note's textarea next tick
      window.setTimeout(() => {
        const el = document.querySelector<HTMLTextAreaElement>(`textarea[data-note="${n.id}"]`);
        el?.focus();
      }, 30);
    } catch (e: any) {
      window.alert(e?.message || 'Could not add a note.');
    } finally { setBusy(false); }
  };

  const patch = (id: string, body: Partial<{ body: string; color: string }>) => {
    setNotes((c) => (c || []).map((n) => (n.id === id ? { ...n, ...body } : n)));
  };

  const saveBodyDebounced = (id: string, text: string) => {
    patch(id, { body: text });
    window.clearTimeout(timers.current[id]);
    timers.current[id] = window.setTimeout(() => {
      api.updateStickyNote(id, { body: text }).catch(() => {});
    }, 650);
  };

  const setColor = (id: string, color: string) => {
    patch(id, { color });
    api.updateStickyNote(id, { color }).catch(() => {});
  };

  const remove = async (id: string) => {
    const prev = notes;
    setNotes((c) => (c || []).filter((n) => n.id !== id));
    try { await api.deleteStickyNote(id); } catch { setNotes(prev); }
  };

  return (
    <div className="fx-chat" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px 6px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 750, color: 'var(--fx-text)' }}>Tasks &amp; Notes</h1>
        <span style={{ flex: 1 }} />
        <button type="button" className="fx-sendbtn" onClick={add} disabled={busy}>+ New note</button>
      </header>
      <div style={{ padding: '0 20px 8px', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--fx-muted)', fontSize: 12.5 }}>
        <Icon name="lock" size={13} />
        <span>Private to you{meName ? `, ${meName}` : ''} — tasks and notes here are yours alone.</span>
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '4px 20px 28px' }}>
        <TasksSection workspaceId={workspaceId ?? null} onOpenChannel={onOpenChannel} />

        {notes === null ? (
          <div style={{ color: 'var(--fx-muted)', fontSize: 14 }}>Loading…</div>
        ) : notes.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', gap: 14, padding: '40px 20px', color: 'var(--fx-muted)' }}>
            <div style={{ width: 72, height: 72, borderRadius: 16, background: PALETTE.yellow.bg, display: 'grid', placeItems: 'center',
              boxShadow: '0 6px 18px rgba(0,0,0,0.14)', transform: 'rotate(-4deg)', color: INK }}>
              <Icon name="note" size={34} />
            </div>
            <div style={{ fontSize: 15.5, fontWeight: 650, color: 'var(--fx-text)' }}>No notes yet</div>
            <div style={{ fontSize: 13.5, maxWidth: 340 }}>A private scratchpad for reminders and snippets. Only you can see what you jot here.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16, alignItems: 'start' }}>
            {notes.map((n) => {
              const pal = PALETTE[n.color] || PALETTE.yellow;
              return (
                <div key={n.id} className="fx-sticky-card"
                  style={{ background: pal.bg, borderRadius: 12, padding: '12px 12px 8px', minHeight: 168,
                    display: 'flex', flexDirection: 'column', boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
                    border: `1px solid ${pal.edge}` }}>
                  <textarea
                    data-note={n.id}
                    value={n.body}
                    onChange={(e) => saveBodyDebounced(n.id, e.target.value)}
                    onBlur={(e) => { window.clearTimeout(timers.current[n.id]); api.updateStickyNote(n.id, { body: e.target.value }).catch(() => {}); }}
                    placeholder="Write a note…"
                    spellCheck
                    style={{ flex: 1, resize: 'none', border: 'none', outline: 'none', background: 'transparent',
                      color: INK, fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit', minHeight: 108, padding: 0 }}
                  />
                  <div className="fx-sticky-foot" style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6,
                    paddingTop: 6, borderTop: `1px solid ${pal.edge}` }}>
                    {COLORS.map((c) => (
                      <button key={c} type="button" title={c} aria-label={`Color ${c}`} onClick={() => setColor(n.id, c)}
                        style={{ width: 15, height: 15, borderRadius: '50%', cursor: 'pointer', padding: 0,
                          background: PALETTE[c].bg, border: n.color === c ? `2px solid ${INK}` : `1px solid ${PALETTE[c].edge}` }} />
                    ))}
                    <span style={{ flex: 1 }} />
                    <button type="button" title="Delete note" aria-label="Delete note" onClick={() => remove(n.id)}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: INK_SOFT, display: 'grid', placeItems: 'center', padding: 3 }}>
                      <Icon name="trash" size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
